<template>
  <div class="relative" ref="dropdownContainer">
    <!-- Trigger Button -->
    <button
      @click.stop="toggleDropdown"
      type="button"
      :class="buttonClasses"
    >
      <span :class="[compact ? 'text-xs truncate' : 'text-sm truncate', { sensitive: sensitiveLabels && modelValue?.length === 1 && !hideSingleValue }]">
        {{ displayText }}
      </span>
      <svg
        :class="[compact ? 'w-3 h-3' : 'w-4 h-4', 'transition-transform ml-1 shrink-0', isOpen ? 'rotate-180' : '']"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
      </svg>
    </button>

    <!-- Dropdown Menu -->
    <Teleport to="body">
      <div
        v-show="isOpen"
        ref="dropdownMenu"
        class="fixed z-[9999] bg-white border border-gray-300 rounded-md shadow-lg max-h-96 overflow-y-auto"
        :style="dropdownStyle"
      >
      <!-- Search + Select All / Clear All -->
      <div class="sticky top-0 bg-gray-50 border-b border-gray-200 px-3 py-2 flex flex-col gap-1.5">
        <input
          v-if="searchable"
          v-model="searchQuery"
          :class="{ sensitive: sensitiveLabels }"
          type="text"
          placeholder="Search..."
          class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-dark"
          @click.stop
          @keydown.stop
        />
        <div class="flex items-center justify-between">
          <button
            @click.stop="selectAll"
            type="button"
            class="text-xs text-primary-600 hover:text-primary-800 font-medium"
          >
            Select All
          </button>
          <button
            @click.stop="clearAll"
            type="button"
            class="text-xs text-gray-600 hover:text-gray-800 font-medium"
          >
            Clear All
          </button>
        </div>
      </div>

      <!-- Options List (grouped) -->
      <div v-if="isGrouped">
        <template v-for="group in groupedOptions" :key="group.name">
          <div
            class="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 select-none cursor-pointer flex items-center justify-between"
            @click.stop="toggleGroup(group.name)"
          >
            <span>{{ group.name }}</span>
            <span class="text-[10px] text-gray-400">
              {{ isGroupExpanded(group.name) ? '▾' : '▸' }} {{ group.options.length }}
            </span>
          </div>
          <template v-if="isGroupExpanded(group.name)">
            <label
              v-for="option in group.options"
              :key="option.value"
              class="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer transition-colors"
              @click.stop
            >
              <input
                type="checkbox"
                :value="option.value"
                :checked="isSelected(option.value)"
                @change="toggleOption(option.value)"
                @click.stop
                class="h-4 w-4 text-primary-600 focus:ring-blue-dark border-gray-300 rounded cursor-pointer"
              />
              <span class="ml-3 text-sm text-gray-700" :class="{ sensitive: sensitiveLabels }">{{ option.label }}</span>
            </label>
          </template>
        </template>
      </div>

      <!-- Options List (flat) -->
      <div v-else class="py-1">
        <label
          v-for="option in filteredOptions"
          :key="option.value"
          class="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer transition-colors"
          @click.stop
        >
          <input
            type="checkbox"
            :value="option.value"
            :checked="isSelected(option.value)"
            @change="toggleOption(option.value)"
            @click.stop
            class="h-4 w-4 text-primary-600 focus:ring-blue-dark border-gray-300 rounded cursor-pointer"
          />
          <span class="ml-3 text-sm text-gray-700" :class="{ sensitive: sensitiveLabels }">{{ option.label }}</span>
        </label>
      </div>

      <!-- Empty State -->
      <div v-if="filteredOptions.length === 0" class="px-3 py-4 text-center text-sm text-gray-500">
        No options available
      </div>
      </div>
    </Teleport>
  </div>
</template>

<script>

export default {
  name: 'MultiSelectDropdown',
  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    options: {
      type: Array,
      required: true,
      // Expected format: [{ value: 'id', label: 'Display Name', group: 'Optional Group' }, ...]
    },
    placeholder: {
      type: String,
      default: 'Select options'
    },
    searchable: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false
    },
    maxWidth: {
      type: String,
      default: null
    },
    hideSingleValue: {
      type: Boolean,
      default: false
    },
    sensitiveLabels: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'change', 'close'],
  data() {
    return {
      uid: Math.random().toString(36).slice(2),
      isOpen: false,
      searchQuery: '',
      expandedGroups: {},
      dropdownStyle: {
        top: '0px',
        left: '0px',
        width: '200px'
      }
    }
  },
  computed: {
    buttonClasses() {
      if (this.compact) {
        return 'inline-flex items-center px-2 py-1 text-xs border border-gray-300 rounded-full bg-white text-gray-500 hover:text-gray-700 hover:border-gray-400 focus:outline-none transition-colors cursor-pointer';
      }
      return 'w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-left focus:outline-none focus:ring-1 focus:ring-blue-dark hover:bg-gray-50 transition-colors flex items-center justify-between';
    },
    filteredOptions() {
      if (!this.searchable || !this.searchQuery) return this.options
      const q = this.searchQuery.toLowerCase()
      return this.options.filter(opt => opt.label.toLowerCase().includes(q))
    },
    isGrouped() {
      return this.filteredOptions.some(opt => opt.group);
    },
    groupedOptions() {
      const groups = [];
      const groupMap = {};
      for (const opt of this.filteredOptions) {
        const name = opt.group || 'Other';
        if (!groupMap[name]) {
          groupMap[name] = { name, options: [] };
          groups.push(groupMap[name]);
        }
        groupMap[name].options.push(opt);
      }
      return groups;
    },
    displayText() {
      if (!this.modelValue || this.modelValue.length === 0) {
        return this.placeholder
      }

      if (this.modelValue.length === 1 && !this.hideSingleValue) {
        const selectedOption = this.options.find(opt => opt.value === this.modelValue[0])
        return selectedOption ? selectedOption.label : this.placeholder
      }

      return `${this.modelValue.length} selected`
    }
  },
  methods: {
    updateDropdownPosition() {
      if (!this.$refs.dropdownContainer) return;

      const rect = this.$refs.dropdownContainer.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      // Measure the real menu height; fall back to the max (max-h-96 = 384px) before first render.
      const menuHeight = (this.$refs.dropdownMenu && this.$refs.dropdownMenu.offsetHeight) || 384;

      const style = {
        position: 'fixed',
        minWidth: '200px',
      };

      const menuWidth = (this.$refs.dropdownMenu && this.$refs.dropdownMenu.offsetWidth) || 200;
      const wouldOverflowRight = rect.left + Math.max(menuWidth, 200) > window.innerWidth - 100;

      if (wouldOverflowRight) {
        style.right = `${window.innerWidth - rect.right}px`;
      } else {
        style.left = `${rect.left}px`;
      }

      // Open upward only when the menu doesn't fit below AND there's more room above. Anchor the
      // menu's BOTTOM to the trigger so it hugs the button whatever its height — the old code
      // assumed a fixed 384px height and left short menus floating far above the trigger.
      if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
        style.bottom = `${window.innerHeight - rect.top + 4}px`;
        style.maxHeight = `${Math.min(384, spaceAbove - 8)}px`;
      } else {
        style.top = `${rect.bottom + 4}px`;
        style.maxHeight = `${Math.min(384, spaceBelow - 8)}px`;
      }

      if (this.compact) {
        style.width = 'auto';
      } else {
        style.width = `${rect.width}px`;
      }

      if (this.maxWidth) {
        style.maxWidth = this.maxWidth;
      }

      this.dropdownStyle = style;
    },
    toggleDropdown() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.expandedGroups = {};
        document.dispatchEvent(new CustomEvent('multiselectdropdown-open', { detail: this.uid }));
        this.$nextTick(() => {
          this.updateDropdownPosition();
        });
      } else {
        // Closing via the trigger button — treat as "done selecting".
        this.$emit('close');
      }
    },
    closeDropdown() {
      const wasOpen = this.isOpen;
      this.isOpen = false
      this.searchQuery = ''
      // Emit only on a real open -> close transition, so consumers can treat it
      // as "done selecting" without spurious fires from stray outside clicks.
      if (wasOpen) this.$emit('close');
    },
    handleOtherOpen(e) {
      if (e.detail !== this.uid && this.isOpen) {
        this.closeDropdown();
      }
    },
    toggleGroup(name) {
      this.expandedGroups = { ...this.expandedGroups, [name]: !this.isGroupExpanded(name) };
    },
    isGroupExpanded(name) {
      if (this.searchQuery) return true;
      if (name in this.expandedGroups) return this.expandedGroups[name];
      return true;
    },
    isSelected(value) {
      return this.modelValue.includes(value)
    },
    toggleOption(value) {
      let newValue
      if (this.isSelected(value)) {
        newValue = this.modelValue.filter(v => v !== value)
      } else {
        newValue = [...this.modelValue, value]
      }

      this.$emit('update:modelValue', newValue)
      this.$emit('change', newValue)
    },
    selectAll() {
      const allValues = this.filteredOptions.map(opt => opt.value)
      const existing = this.modelValue.filter(v => !this.filteredOptions.find(o => o.value === v))
      const merged = [...existing, ...allValues]
      this.$emit('update:modelValue', merged)
      this.$emit('change', merged)
    },
    clearAll() {
      this.$emit('update:modelValue', [])
      this.$emit('change', [])
    },
    handleClickOutside(event) {
      const container = this.$refs.dropdownContainer;
      const menu = this.$refs.dropdownMenu;
      if (container && !container.contains(event.target) &&
          (!menu || !menu.contains(event.target))) {
        this.closeDropdown()
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    document.addEventListener('multiselectdropdown-open', this.handleOtherOpen);
    window.addEventListener('scroll', this.updateDropdownPosition, true);
    window.addEventListener('resize', this.updateDropdownPosition);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('multiselectdropdown-open', this.handleOtherOpen);
    window.removeEventListener('scroll', this.updateDropdownPosition, true);
    window.removeEventListener('resize', this.updateDropdownPosition);
  }
}
</script>

<style scoped>
/* Custom scrollbar for dropdown */
.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
